import { useCallback, type KeyboardEvent, type MouseEvent } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { mergeClasses } from '@fluentui/react-components';
import type { RailSubItem } from '@/types/leftNavRail.types';
import { useNavRailSubItemStyles } from './NavRailSubItem.styles';

export interface NavRailSubItemProps {
  readonly subItem: RailSubItem;
  readonly isActive: boolean;
  readonly onSelect: (subItem: RailSubItem, event: MouseEvent | KeyboardEvent) => void;
  readonly role?: 'menuitem' | 'none';
}

export const NavRailSubItem = ({ subItem, isActive, onSelect, role }: NavRailSubItemProps) => {
  const styles = useNavRailSubItemStyles();
  const { t } = useTranslation();
  const Icon = isActive ? subItem.iconActive : subItem.icon;
  const label = t(subItem.labelKey);

  const handleClick = useCallback(
    (event: MouseEvent<HTMLAnchorElement>) => {
      onSelect(subItem, event);
    },
    [subItem, onSelect]
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLAnchorElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        onSelect(subItem, event);
      }
    },
    [subItem, onSelect]
  );

  return (
    <Link
      to={subItem.route}
      className={mergeClasses(styles.root, isActive && styles.rootActive)}
      data-testid={subItem.testId}
      data-rail-subitem-key={subItem.key}
      aria-current={isActive ? 'page' : undefined}
      role={role}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      <span className={styles.iconColumn} aria-hidden="true">
        <Icon />
      </span>
      <span className={styles.label}>{label}</span>
    </Link>
  );
};

NavRailSubItem.displayName = 'NavRailSubItem';
